const MEZUZAH_MATERIALS = [
  {
    key: 'wood',
    href: '/s/judaica/mezuzas?pub_j_materials=has_all%3Awood',
    title: 'Wood Mezuzah Cases',
  },
  {
    key: 'resin_epoxy',
    href: '/s/judaica/mezuzas?pub_j_materials=has_all%3Aresin',
    title: 'Resin / Epoxy Mezuzah Cases',
  },
  {
    key: 'jerusalem_stone',
    href: '/s/judaica/mezuzas?pub_j_materials=has_all%3Astone',
    title: 'Jerusalem Stone Mezuzah Cases',
  },
  {
    key: 'porcelain',
    href: '/s/judaica/mezuzas?pub_j_materials=has_all%3Aporcelain-earthenware',
    title: 'Porcelain / Earthenware Mezuzah Cases',
  },
  {
    key: 'hand_painted',
    href: '/s/judaica/mezuzas?pub_j_materials=has_all%3Ahandpainted',
    title: 'Hand-Painted Mezuzah Cases',
  },
];

const getSchema = rootURL => {
  const mezuzahURL = `${rootURL}/s/judaica/mezuzas`;
  return [
    {
      '@type': 'CollectionPage',
      '@id': `${rootURL}/s/judaica#mezuzah-webpage`,
      url: mezuzahURL,
      name: 'Mezuzah Cases | Handmade Ceramic, Wood & Stone | Joodaic',
      description:
        'Mezuzah cases for indoor and outdoor use in wood, stone, ceramic, and more. Find the right size, material, and fit for your door — sold by Jewish artists.',
      isPartOf: { '@id': `${rootURL}/#website` },
      about: [
        { '@type': 'Thing', name: 'Mezuzah Cases' },
        { '@type': 'Thing', name: 'Outdoor Mezuzah Cases' },
        { '@type': 'Thing', name: 'Handmade Mezuzah' },
        { '@type': 'Thing', name: 'Jewish Home Decor' },
      ],
      publisher: { '@id': `${rootURL}/#organization` },
      mainEntity: { '@id': `${rootURL}/s/judaica#mezuzah-materials` },
    },
    {
      '@type': 'ItemList',
      '@id': `${rootURL}/s/judaica#mezuzah-materials`,
      name: 'Mezuzah Case Materials',
      itemListElement: MEZUZAH_MATERIALS.map((mat, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        name: mat.title,
        url: `${rootURL}${mat.href}`,
      })),
    },
    {
      '@type': 'FAQPage',
      '@id': `${rootURL}/s/judaica#mezuzah-faq`,
      mainEntity: [
        {
          '@type': 'Question',
          name: 'What is the difference between a mezuzah and a mezuzah case?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'A mezuzah refers to the parchment scroll (klaf) with Hebrew text. The mezuzah case is the outer cover that holds and protects the scroll on the doorpost.',
          },
        },
        {
          '@type': 'Question',
          name: 'Do I need different mezuzah cases for different doorways?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'You can use different cases for different doorways. Many people choose a more durable or decorative case for the main entrance and simpler ones for interior rooms.',
          },
        },
        {
          '@type': 'Question',
          name: 'What material is best for a mezuzah case?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'It depends on where it will be used. For outdoor use, more durable materials or sealed finishes are usually a better choice. For indoor use, you have more flexibility to choose based on style.',
          },
        },
        {
          '@type': 'Question',
          name: 'What size mezuzah case should I choose?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'The case needs to fit the scroll you plan to use. Listings usually include size details, and many artists offer multiple sizes or custom options.',
          },
        },
        {
          '@type': 'Question',
          name: 'Are mezuzah cases suitable as gifts?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Yes. Mezuzah cases are one of the most common Jewish gifts, especially for new homes, weddings, and housewarmings.',
          },
        },
        {
          '@type': 'Question',
          name: 'Can a mezuzah case be customized or made to order?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: "Many mezuzah cases can be customized — whether that's size, finish, or design. Some artists also take on made-to-order work for specific requests. You can message the artist directly through their storefront, or use the personalization option where available.",
          },
        },
      ],
    },
  ];
};

const mezuzahSeoConfig = {
  key: 'mezuzah',
  match: ({ listingType, activeJudaicaCollection }) =>
    listingType === 'judaica' && activeJudaicaCollection?.includes('mezuzas'),
  accentColor: '--colorJudaica',
  links: MEZUZAH_MATERIALS,
  title: 'Mezuzah Cases | Handmade Ceramic, Wood & Stone | Joodaic',
  description:
    'Mezuzah cases for indoor and outdoor use in wood, stone, ceramic, and more. Find the right size, material, and fit for your door — sold by Jewish artists.',
  getSchema,
  intro: {
    heading: 'Mezuzah Cases for the Doorway',
    tagline:
      'Mezuzah cases in wood, stone, ceramic, glass, and hand-finished materials — designed for both indoor and outdoor use, and made by independent Jewish artists.',
    collapsedHeading: 'Mezuzah Cases',
    bodyParagraphs: [
      "Most people aren't looking for \"a mezuzah case.\" They're trying to figure out what works for their door.",
      "If it's going outside, it needs to handle weather. If it's inside, it's more about how it sits with the space. From there, it comes down to material, size, and whether you want something simple or something you'll notice every time you walk in.",
      "For some homes, the front-door mezuzah is something you pick once and forget. For others, it's something you choose carefully.",
    ],
    linksLabel: 'Explore Mezuzah Cases by Material',
  },
  bottomSections: [
    {
      id: 'intro',
      type: 'prose',
      heading: 'How to choose a mezuzah case',
      paragraphs: ['Most buyers are working through the same few decisions:'],
      list: [
        'Where it\'s going — front door vs interior',
        'What it\'s made from — affects durability and feel',
        'What size you need — depends on the scroll',
      ],
      paragraphsAfter: ['Start with where it\'s going. That usually narrows everything else.'],
    },
    {
      id: 'materials',
      title: 'Choosing between materials',
      paragraphs: ['Each material brings a different balance of durability and look:'],
      list: [
        'Wood — warm and natural, works well inside the home',
        'Jerusalem stone — solid and traditional, often used for entrances',
        'Resin / epoxy — more expressive, with color and movement',
        'Porcelain / earthenware — refined and more delicate, suited for interior spaces',
        'Glass — clean and decorative, typically chosen for interior use',
        'Hand-painted — ceramic, porcelain, or stone cases finished by hand, chosen for design over durability',
      ],
      paragraphsAfter: [
        'For outdoor use, sturdier materials or sealed finishes tend to hold up better over time.',
      ],
    },
    {
      id: 'indoor_outdoor',
      title: 'Indoor vs outdoor mezuzah cases',
      paragraphs: ['Not every mezuzah case is suited for outdoor use.'],
      list: [
        'Outdoor cases need to handle sun, rain, and temperature changes without damaging the scroll',
        'Indoor cases give you more flexibility to choose based on design and material',
      ],
      paragraphsAfter: [
        "If you're buying for a main entrance, it's worth checking that the case is intended for outdoor placement.",
      ],
    },
    {
      id: 'size',
      title: 'Size and scroll compatibility',
      paragraphs: [
        'Mezuzah cases come in different sizes, and the case needs to fit the scroll (klaf) inside.',
      ],
      list: [
        'Smaller cases are typically used for interior doorways',
        'Larger ones are more common for front doors',
      ],
      paragraphsAfter: [
        "Some listings include multiple size options, and many artists offer custom sizing. If you're unsure, check the listing or ask the artist directly.",
      ],
    },
    {
      id: 'scroll',
      title: 'Do mezuzah cases include the scroll?',
      paragraphs: [
        'In most cases, the mezuzah case is sold without the scroll.',
        'The kosher scroll (klaf) is usually purchased separately from a sofer. Listing pages will note if a scroll is included.',
      ],
    },
    {
      id: 'artists',
      title: 'Made by independent Jewish artists',
      paragraphs: [
        'Each mezuzah case on Joodaic is created by an independent Jewish artist. Some are made in small batches, others as part of an ongoing studio practice.',
        "That's why the range of materials, finishes, and styles is broader than what you'll find in standard retail collections.",
      ],
    },
    {
      id: 'custom',
      title: 'Custom and personalized mezuzah cases',
      paragraphs: [
        'Many mezuzah cases can be customized — whether that\'s size, finish, or design.',
        'For specific requests, you can message the artist directly through their storefront. Some listings also allow you to send details at checkout.',
        'Many homes pair a mezuzah case with <a href="/s/judaica/nameplates">door name plates</a> for a more complete entrance.',
      ],
    },
  ],
  faqHeading: 'FAQ',
  faq: [
    {
      question: 'What is the difference between a mezuzah and a mezuzah case?',
      answer: [
        'A mezuzah refers to the parchment scroll (klaf) with Hebrew text. The mezuzah case is the outer cover that holds and protects the scroll on the doorpost.',
      ],
    },
    {
      question: 'Do I need different mezuzah cases for different doorways?',
      answer: [
        'You can use different cases for different doorways. Many people choose a more durable or decorative case for the main entrance and simpler ones for interior rooms.',
      ],
    },
    {
      question: 'What material is best for a mezuzah case?',
      answer: [
        'It depends on where it will be used.',
        'For outdoor use, more durable materials or sealed finishes are usually a better choice. For indoor use, you have more flexibility to choose based on style.',
      ],
    },
    {
      question: 'What size mezuzah case should I choose?',
      answer: [
        'The case needs to fit the scroll you plan to use. Listings usually include size details, and many artists offer multiple sizes or custom options.',
      ],
    },
    {
      question: 'Are mezuzah cases suitable as gifts?',
      answer: [
        'Yes. Mezuzah cases are one of the most common Jewish gifts, especially for new homes, weddings, and housewarmings.',
      ],
    },
    {
      question: 'Can a mezuzah case be customized or made to order?',
      answer: [
        "Many mezuzah cases can be customized — whether that's size, finish, or design. Some artists also take on made-to-order work for specific requests.",
        'You can message the artist directly through their storefront, or use the personalization option where available.',
      ],
    },
  ],
};

export default mezuzahSeoConfig;
